import { CATEGORY } from 'src/refactor/helpers/constants'
import { TGetCarepointFacilityByNetworkLatAndLongV2ProviderSummary } from 'src/shared/apiResponseTypes'
import {
  IFacility,
  IPractitioner,
  IPractice,
  TGenderOptions,
  TSearchType,
} from 'src/shared/types'

export interface PlanNetwork {
  clientId: string
  planId: string
  planName: string
  maps: string
  requireVpcModule?: boolean
}

export interface PlanNetworkMap {
  planName: string
  networks: string[]
}

export interface PlanMap {
  planName: string
  maps: string
}

export type PractitionerSummary =
  TGetCarepointFacilityByNetworkLatAndLongV2ProviderSummary

export interface Specialty {
  code: string
  specialty: string
  isCommonSpecialty: boolean
  isFacility: boolean
  isMember: boolean
  isOe: boolean
  isOeDoctor: boolean
  isOeFacility: boolean
  isProvider: boolean
  level1: string
  level2: string
}

export interface Specialties {
  name: string
  code: string
}

export interface ServiceLine {
  codes: string[]
  isOeDoctor: boolean
  isOeFacility: boolean
  isFilter: boolean
  name: string
}

export interface SearchParams {
  zipCode?: string
  category: CATEGORY
  distance?: number
  serviceLines?: string
  name?: string
  practiceName?: string
  gender?: TGenderOptions
  newPatients?: boolean
  withVirtualCare?: boolean
  specialtyFilters?: string[]
  placeId: string
  addressLabel: string
}

export type GetProvidersSearch = Pick<
  SearchParams,
  | 'name'
  | 'serviceLines'
  | 'practiceName'
  | 'zipCode'
  | 'placeId'
  | 'addressLabel'
>

export interface SearchProvidersByPracticeIdParams extends GetProvidersSearch {
  practiceId: string
}

export type GetProvidersFilter = Required<
  Pick<
    SearchParams,
    | 'distance'
    | 'gender'
    | 'newPatients'
    | 'withVirtualCare'
    | 'specialtyFilters'
  > & {
    providerType: 'all' | Exclude<TSearchType, 'facility'>
  }
>

export interface GetPractitionersParams {
  search: GetProvidersSearch
  filter: GetProvidersFilter
  pos: {
    lat: number
    long: number
  }
  isVirtualCareOnly?: boolean
}

export interface GetPracticeOpenSearch {
  count: {
    totalPractices: number
    totalProviders: number
    specialties: { code: string; total: number }[]
    virtualOption: {
      n: number | undefined
      y: number | undefined
      only: number | undefined
    }
    gender: {
      m: number | undefined
      f: number | undefined
      unknown: number | undefined
    }
    isAcceptingNewPatients: number
  }
  specialtyFilters: Specialties[]
  data: IPractice[]
  meta: {
    distance: string | undefined
    lat: number
    long: number
    networks: string[]
    serviceLineSpecialties: string[] | undefined
    specialties: string[] | undefined
    zipCode: number
  }
}

export interface GetProviderOpenSearch {
  count: {
    total: number
    specialties: { code: string; total: number }[]
    virtualOption: {
      n: number | undefined
      y: number | undefined
      only: number | undefined
    }
    gender: {
      m: number | undefined
      f: number | undefined
      unknown: number | undefined
    }
    isAcceptingNewPatients: number
  }
  specialtyFilters: Specialties[]
  data: IPractitioner[]
  meta: {
    distance: string | undefined
    lat: number
    long: number
    networks: string[]
    serviceLineSpecialties: string[] | undefined
    specialties: string[] | undefined
    zipCode: number
  }
}

export interface GetDoctors {
  specialtyFilters: Specialties[]
  count: {
    total: number
    virtualOption: {
      n: number | undefined
      y: number | undefined
      only: number | undefined
    }
    isAcceptingNewPatients: number
  }
  data: {
    practices: GetPracticeOpenSearch['data']
    providers: GetProviderOpenSearch['data']
  }
}

export interface GetProvidersByPracticeIdOpenSearch {
  count: {
    total: number
    gender: {
      m: number | undefined
      f: number | undefined
      unknown: number | undefined
    }
  }
  data: IPractitioner[]
  meta: {
    pageSize: number
  }
}

export interface GetFacilities {
  distance: string
  networks: string[]
  serviceLines: string[]
  placeId: string
  specialties: string
  data: IFacility[]
  specialtyFilters: { name: string; code: string }[]
  count: {
    total: number
  }
}

export interface GetPractitioners {
  count: {
    total: number
    isAcceptingNewPatients: boolean | null
    female: number
    genderNotInformed: number
    male: number
    virtualCare: { available: number; only: number; unavailable: number }
  }
  data: PractitionerSummary[]
  isPcp: boolean
  latitude: string
  longitude: string
  networks: string[]
  practiceName: string
  specialties: string
  virtualCareAvailable: boolean
  virtualCareOnly: boolean
  virtualCareUnavailable: boolean
}

export interface NominatimResponse {
  address: {
    country: string
    country_code: string
    postcode: string
    state: string
  }
  boundingBox: [string, string, string, string]
  display_name: string
  lat: string
  licence: string
  lon: string
  osm_id: number
  osm_type: string
  place_id: number
}

export type OE_TEMPLATES =
  | 'template-1'
  | 'template-2'
  | 'template-3'
  | 'template-eligibility'
  | 'template-4'
  | 'template-underConstruction'

export const CONFIG_KEYS = [
  'DEBUG_USE_ELIGIBILITY_LIST',
  'ELIGIBILITY_LINK_URL',
  'ELIGIBILITY_LIST_NAME',
  'ELIGIBILITY_ZIP_CODES',
  'FOOTER_TEXT',
  'FORMAT_PHONE',
  'REACT_APP_CAREPOINT_URL',
  'REACT_APP_LOCATION_SERVICE_URL',
  'REACT_APP_CUSTOM_STYLE',
  'REACT_APP_GEOLOCATION_API_ROOT',
  'REACT_APP_HEADER_TEXT',
  'REACT_APP_HERO_IMAGE',
  'REACT_APP_LOGO',
  'REACT_APP_MAPBOX_ACCESS_TOKEN',
  'REACT_APP_NETWORK_OF_EXCELLENCE',
  'REACT_APP_NETWORK_PLAN_SELECTION',
  'REACT_APP_PHONE',
  'REACT_APP_PLAN_NETWORKS',
  'REACT_APP_PROVIDER_NETWORK_MAPS',
  'REACT_APP_PROVIDER_PORTAL_API_URL',
  'REACT_APP_REVERSE_GEOLOCATION_API_ROOT',
  'REACT_APP_TEMPLATE',
  'REACT_APP_WITH_CENTIVO_WILL_GET',
  'SHOW_ALREADY_MEMBER',
  'SHOW_CONTACT_FORM',
  'SHOW_DISCLAIMER',
  'SHOW_ELIGIBILITY',
  'SHOW_ELIGIBILITY_LINK',
  'SHOW_HEADER',
  'SHOW_HERO',
  'SHOW_MAIN_INFO',
  'SHOW_NETWORK_OF_EXCELLENCE',
  'SHOW_NUMBER_ON_MENU',
  'SHOW_SEARCH',
  'SHOW_TESTIMONIALS',
  'SHOW_VPC',
  'DISPLAY_PCP_ACTIVATION_BADGE',
  'DISPLAY_REFERRAL_REQUIRED_BADGE',
  'CENTIVO_CARE',
  'REACT_APP_PLAN_MANAGER_API_URL',
  'REQUIRE_VPC_MODULE',
  'PAGE_TITLE',
] as const

export interface AppConfig
  extends Partial<Record<(typeof CONFIG_KEYS)[number], unknown>> {
  DEBUG_USE_ELIGIBILITY_LIST?: string
  ELIGIBILITY_LINK_URL: string
  ELIGIBILITY_LIST_NAME: string
  ELIGIBILITY_ZIP_CODES: string[]
  FOOTER_TEXT?: string
  FORMAT_PHONE: string
  REACT_APP_CAREPOINT_URL: string
  REACT_APP_LOCATION_SERVICE_URL: string
  REACT_APP_CUSTOM_STYLE: string
  REACT_APP_GEOLOCATION_API_ROOT: string
  REACT_APP_HEADER_TEXT: string
  REACT_APP_HERO_IMAGE: string
  REACT_APP_PREFERRED_NETWORK_CODES: string[]
  REACT_APP_LOGO?: string
  REACT_APP_MAPBOX_ACCESS_TOKEN: string
  REACT_APP_NETWORK_OF_EXCELLENCE: string[]
  REACT_APP_NETWORK_PLAN_SELECTION: string
  REACT_APP_PHONE: string
  REACT_APP_PLAN_NETWORKS: PlanNetwork[]
  REACT_APP_PROVIDER_NETWORK_MAPS: string
  REACT_APP_PROVIDER_PORTAL_API_URL: string
  REACT_APP_REVERSE_GEOLOCATION_API_ROOT: string
  REACT_APP_TEMPLATE: OE_TEMPLATES
  REACT_APP_WITH_CENTIVO_WILL_GET: string[]
  REACT_APP_HERO_BTN?: {
    enabled: boolean
    text: string
    link: string
  }
  REACT_APP_EXTERNAL_LINK: {
    enabled: boolean
    url: string
    label: string
  }
  REACT_APP_DESCRIPTION: string
  SHOW_ALREADY_MEMBER?: string | boolean
  SHOW_CONTACT_FORM: string | boolean
  SHOW_DISCLAIMER: string | boolean
  SHOW_ELIGIBILITY?: string | boolean
  SHOW_ELIGIBILITY_LINK: string | boolean
  SHOW_HEADER?: string | boolean
  SHOW_HERO?: string | boolean
  SHOW_MAIN_INFO: string | boolean
  SHOW_NETWORK_OF_EXCELLENCE: string | boolean
  SHOW_NUMBER_ON_MENU: string | boolean
  SHOW_SEARCH?: string | boolean
  SHOW_TESTIMONIALS: string | boolean
  SHOW_VPC: string | boolean
  DISPLAY_PCP_ACTIVATION_BADGE?: string | boolean
  DISPLAY_REFERRAL_REQUIRED_BADGE?: string | boolean
  CENTIVO_CARE: {
    text: string
    link: string
    title: string
    body: string
  }
  CENTIVO_CARE_ENABLED: boolean
  REACT_APP_PLAN_MANAGER_API_URL: string
  REQUIRE_VPC_MODULE?: boolean
  VIDEO_URL?: string
  VIDEO_ENABLED?: boolean
  PAGE_TITLE?: string
}
